/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { ChangeEvent } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { AdjustmentsIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Select, Theme } from '@nexigendigital/nxui'
import { useUpdateCartItemsMutation } from 'api/shop'
import { updateConfigOptions } from 'store/slices/shopSlice'
import { Weight } from 'models/enums'
import { CartItem, ConfigOptionGroup } from 'models/shop'
import { ConfigOption } from 'models/hosting'
import { useAppDispatch, useAppSelector } from 'store/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    item: CartItem,
    id: number | null,
    configOption: ConfigOption,
    hosting: null | {
        config_option_groups: ConfigOptionGroup[]
    }
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Options({ item, id, configOption, hosting }: Props) {
    const { pathname } = useLocation()
    const dispatch = useAppDispatch()
    const { cart, cartUpdateStatus } = useAppSelector((state) => state.shop)
    const { config_id, option_id } = configOption
    const { config_options }: any = hosting?.config_option_groups[0]
    const {
        values,
        name: configName
    }: any = config_options.find((config_option: { id: number }) => config_option.id === config_id)
    const [updateCartItem] = useUpdateCartItemsMutation()
    const currentOption = values.find((value: { id: number, name: string }) => value.id === option_id)

    function synchroniseWithCart(config: { config_id: number, option_id: number, product_id: number | null }) {
        if (cart) {
            const updateItems = cart.items.map((cartItem: CartItem) => {
                if (cartItem && cartItem.product_id === item.product_id && cartItem.name === item.name && Array.isArray(cartItem.config_options)) {
                    return {
                        ...cartItem,
                        config_options: cartItem.config_options.map((config_option) => {
                            if (config_option.config_id === config.config_id) {
                                return {
                                    ...config_option,
                                    option_id: config.option_id
                                }
                            }

                            return config_option
                        })
                    }
                }

                return cartItem
            })

            updateCartItem({ uuid: cart.uuid, items: updateItems })
        }
    }

    function onOptionChanged(e: ChangeEvent<{ value: string }>) {
        dispatch(updateConfigOptions({
            product_id: id,
            config_id,
            option_id: parseInt(e.target.value, 10)
        }))
        synchroniseWithCart({
            config_id,
            product_id: id,
            option_id: parseInt(e.target.value, 10)
        })
    }

    if (pathname === '/order') {
        return (
            <OrderOptions>
                <AdjustmentsIcon/>
                <div>
                    {currentOption.name} {configName}
                </div>
            </OrderOptions>
        )
    }

    return (
        <Option>
            <AdjustmentsIcon/>
            <Select
                value={option_id}
                disabled={cartUpdateStatus === 'loading'}
                onChange={onOptionChanged}
            >
                {values.map((value: { id: number, name: string }) => (
                    <option key={value.id} value={value.id}>
                        {value.name} {configName}
                    </option>)
                )}
            </Select>
        </Option>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Option = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    ${Theme.Functions.setFont('Inter', Weight.Medium)};

    &:first-child {
        padding-top: 0;
    }

    &:last-child {
        padding-bottom: 0;
    }

    > svg {
        width: 16px;
        height: 16px;
        color: ${props => props.theme['icon--100']}
    }
`

const OrderOptions = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 6px 0;
    ${Theme.Functions.setFont('Inter', Weight.Medium)};

    &:first-child {
        padding-top: 12px;
    }

    &:last-child {
        padding-bottom: 0;
    }

    > svg {
        width: 16px;
        height: 16px;
        color: ${props => props.theme['icon--100']}
    }
`
